import { useEffect, useMemo, useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { useLocation } from "wouter";
import { MessageSquare, Bot, User, UserCog, Phone, Inbox, Send, AlertTriangle, AlertCircle, CheckCircle2, Tag, PackageCheck, Users, Smile, Lock } from "lucide-react";
import { formatDistanceToNow } from "date-fns";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { apiRequest } from "@/lib/queryClient";
import { useToast } from "@/hooks/use-toast";
import { useOrganization } from "@/contexts/OrganizationContext";
import { useAdminBasePath } from "@/lib/adminPath";
import { CowboyHatIcon } from "@/components/cowboy-hat-icon";
import { EscalationStrip, type CatalogProduct } from "@/components/clyde-escalations";
import { displayPhone, ordersPageOrderPath } from "@/lib/clydeThread";
import { asideFrom, asideLink, asideNote, asideRepName, asideStatus, channelLabel, isReactionRow, isSideConversation, orderIdOfMessage, repLabel, sentFromConsole, sideComposePlaceholder, sideRepLabel, splitOrderMentions, startedByLabel, voiceOf } from "@/lib/clydeConsole";
import { placedOrderIdOf } from "@/lib/clydeConsole";
import { consoleEscalations, needsInputLine, openCountByConversation, placeEscalations } from "@/lib/clydeEscalations";
import type { AgentConversation, AgentMessage, AgentEscalation } from "@shared/schema";

/**
 * Clyde — the oversight console.
 *
 * One conversation per buyer over agent_conversations / agent_messages, with the
 * Clyde thread it runs on (docs/clyde/group-thread-transport.md): three voices —
 * the buyer, the rep, Clyde — rendered distinctly, members and strangers muted,
 * tapbacks folded into a marker. Clyde's "Order #N" is a link to that order on
 * the orders page; the orders page's text bubble links back here (`?conversation=`
 * + `?message=` scroll to the message that became the order).
 *
 * The compose box sends AS THE REP ("Kyle: …", from Clyde's number) — never as
 * Clyde, and never under a free-text name: the name is the thread's rep record or
 * the org's supervisor name. The row is stored as the rep's message, so the
 * transport's recap rules treat it exactly like a text from the rep's phone.
 *
 * Reads are bounded + org-scoped server-side (GET /agent/conversations,
 * /agent/conversations/:id).
 */

interface ThreadSummary {
  id: number;
  status: string;
  channel: string;
  buyerAddress: string;
  repAddress: string | null;
  repName: string | null;
  createdBy: string;
  createdAt: string;
  lastInboundAt: string | null;
}

interface ThreadResponse {
  conversation: AgentConversation;
  messages: AgentMessage[];
  agentName: string | null;
  thread: ThreadSummary | null;
  /** The first name the compose box sends as, or null when none is on file. */
  sendAs: string | null;
}

/** A list row: the conversation plus its thread's state (GET /agent/conversations). */
type ConversationListItem = AgentConversation & {
  thread: {
    id: number;
    status: string;
    channel: string;
    repAddress: string | null;
    repName: string | null;
    createdBy: string;
    lastInboundAt: string | null;
  } | null;
};

/**
 * The message's text with "order #N" turned into a link to that order's lightbox;
 * a recap that became an order ends with a link back to it.
 */
function renderMessageContent(msg: AgentMessage, orderHref: (orderId: number) => string, onOpenOrder: (orderId: number) => void) {
  const placedOrderId = placedOrderIdOf(msg);
  if (placedOrderId != null) {
    return (
      <>
        {msg.content}
        <a
          href={orderHref(placedOrderId)}
          onClick={(e) => {
            e.preventDefault();
            onOpenOrder(placedOrderId);
          }}
          className="mt-1.5 flex items-center gap-1 text-xs font-medium text-violet-700 underline decoration-violet-400 underline-offset-2 hover:text-violet-900"
          data-testid="message-placed-order-link"
          data-order-id={placedOrderId}
        >
          <PackageCheck className="h-3 w-3" />
          Became order #{placedOrderId}
        </a>
      </>
    );
  }
  if (orderIdOfMessage(msg) == null) return msg.content;
  return splitOrderMentions(msg.content ?? "").map((part, i) => {
    if (typeof part === "string") return <span key={i}>{part}</span>;
    return (
      <a
        key={i}
        href={orderHref(part.orderId)}
        onClick={(e) => {
          e.preventDefault();
          onOpenOrder(part.orderId);
        }}
        className="font-medium underline decoration-violet-400 underline-offset-2 hover:text-violet-700"
        data-testid="message-order-link"
        data-order-id={part.orderId}
      >
        {part.text}
      </a>
    );
  });
}

// A learned-alias suggestion awaiting review (GET /agent/alias-suggestions).
interface AliasSuggestion {
  productId: number;
  productName: string;
  alias: string;
  seenCount: number;
  at: string | null;
}

function conversationTitle(c: AgentConversation): string {
  return c.customerName?.trim() || c.customerPhone;
}

function relativeTime(value: string | Date | null | undefined): string {
  if (!value) return "";
  const d = typeof value === "string" ? new Date(value) : value;
  if (Number.isNaN(d.getTime())) return "";
  return formatDistanceToNow(d, { addSuffix: true });
}

type Attribution = {
  label: string;
  Icon: typeof Bot;
  side: "left" | "right" | "center";
  bubbleClass: string;
  labelClass: string;
  /** A line under the bubble — an aside says who saw it. */
  note?: string;
};

/**
 * Who said it, and how it renders (voiceOf in lib/clydeConsole.ts decides who).
 * The buyer and the rep sit on the left (people), Clyde on the right (the bot);
 * member and system rows are muted in the middle.
 */
function attributeMessage(msg: AgentMessage, buyerName: string, agentName: string | null, repName?: string | null, sideThread = false): Attribution {
  switch (voiceOf(msg)) {
    case "buyer":
      return { label: buyerName, Icon: User, side: "left", bubbleClass: "bg-gray-100 text-gray-900", labelClass: "text-gray-500" };
    case "clyde":
      return { label: agentName || "Clyde", Icon: Bot, side: "right", bubbleClass: "bg-violet-50 text-violet-900 border border-violet-100", labelClass: "text-violet-600" };
    case "rep": {
      const name = repLabel(msg, repName);
      return {
        label: sentFromConsole(msg) ? `${name} (from the console)` : name,
        Icon: UserCog,
        side: "left",
        bubbleClass: "bg-blue-50 text-blue-900 border border-blue-100",
        labelClass: "text-blue-600",
      };
    }
    case "aside": {
      const rep = asideRepName(msg, repName) || "Rep";
      const clyde = agentName || "Clyde";
      const fromRep = asideFrom(msg) === "rep";
      if (sideThread) {
        // In the rep's own 1:1 with Clyde every message is private — plain bubbles.
        return fromRep
          ? { label: sideRepLabel(msg, rep), Icon: UserCog, side: "left", bubbleClass: "bg-blue-50 text-blue-900 border border-blue-100", labelClass: "text-blue-600" }
          : { label: clyde, Icon: Bot, side: "right", bubbleClass: "bg-violet-50 text-violet-900 border border-violet-100", labelClass: "text-violet-600" };
      }
      // The group thread's one copy of a rep check: dashed amber with a lock, so it
      // never reads as something the buyer saw, and it opens the rep's 1:1.
      return {
        label: `${clyde} → ${rep}, privately`,
        Icon: Lock,
        side: "right",
        bubbleClass: "bg-amber-50 text-amber-950 border border-dashed border-amber-400",
        labelClass: "text-amber-700",
        note: asideNote(msg, rep),
      };
    }
    case "member":
      return { label: "Someone else in the thread", Icon: Users, side: "center", bubbleClass: "bg-transparent text-gray-400", labelClass: "text-gray-400" };
    default:
      return { label: "System", Icon: MessageSquare, side: "center", bubbleClass: "bg-transparent text-gray-400", labelClass: "text-gray-400" };
  }
}

export default function OversightConsole() {
  const { organization } = useOrganization();
  const queryClient = useQueryClient();
  const { toast } = useToast();
  const [, setLocation] = useLocation();
  const adminBase = useAdminBasePath();
  // `?conversation=<id>` opens the console on one thread (the account page, the
  // "Start a Clyde thread" toast and the orders page's text bubble link here);
  // `?message=<id>` scrolls to one message. Otherwise the newest is selected below.
  const [selectedId, setSelectedId] = useState<number | null>(() => {
    const raw = new URLSearchParams(window.location.search).get("conversation");
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  });
  const [focusMessageId, setFocusMessageId] = useState<number | null>(() => {
    const raw = new URLSearchParams(window.location.search).get("message");
    const n = raw ? parseInt(raw, 10) : NaN;
    return Number.isFinite(n) && n > 0 ? n : null;
  });
  const [reply, setReply] = useState("");
  const [sideReply, setSideReply] = useState("");

  const conversationsQuery = useQuery<ConversationListItem[]>({
    queryKey: ["/api/agent/conversations"],
  });
  const conversations = conversationsQuery.data ?? [];

  // Auto-select the most-recently-active conversation once the list arrives.
  useEffect(() => {
    if (selectedId == null && conversations.length > 0) {
      setSelectedId(conversations[0].id);
    }
  }, [conversations, selectedId]);

  const threadQuery = useQuery<ThreadResponse>({
    queryKey: ["/api/agent/conversations", selectedId],
    enabled: selectedId != null,
  });
  const thread = threadQuery.data;
  // Clyde's private 1:1 with a rep (every text he sends them about a buyer).
  const sideThread = thread ? isSideConversation(thread.messages, !!thread.thread) : false;
  const sideRepName = sideThread && thread ? asideRepName(thread.messages.find((m) => m.role === "aside") ?? { role: "aside" }) : null;

  // Deep link to one message: scroll it into view and ring it once the thread is
  // on screen; a message that is not in this thread (or past the bounded read)
  // leaves the thread at its end. Picking another conversation clears the focus.
  const messagesRef = useRef<HTMLDivElement | null>(null);
  useEffect(() => {
    if (!thread || focusMessageId == null) return;
    const el = messagesRef.current?.querySelector<HTMLElement>(`[data-message-id="${focusMessageId}"]`);
    if (el) el.scrollIntoView({ block: "center" });
  }, [thread, focusMessageId]);
  const selectConversation = (id: number) => {
    if (id !== selectedId) setFocusMessageId(null);
    setSelectedId(id);
  };
  // An aside's link: over to the rep's 1:1 with Clyde, or back to the buyer's thread.
  const jumpTo = (id: number, messageId: number | null) => {
    setSelectedId(id);
    setFocusMessageId(messageId);
  };
  const openOrder = (orderId: number) => setLocation(ordersPageOrderPath(adminBase, orderId));

  const replyMutation = useMutation({
    // Thread the target id through the mutation so onSuccess invalidates the
    // conversation the reply actually went to — not whatever is selected by the
    // time the request resolves (the admin may have clicked away mid-send).
    mutationFn: async ({ id, content }: { id: number; content: string }) => {
      const res = await apiRequest("POST", `/api/agent/conversations/${id}/reply`, { content });
      return res.json() as Promise<{ warning?: string } | AgentMessage>;
    },
    onSuccess: (data, variables) => {
      setReply("");
      queryClient.invalidateQueries({ queryKey: ["/api/agent/conversations", variables.id] });
      queryClient.invalidateQueries({ queryKey: ["/api/agent/conversations"] });
      // A take-over resolves the conversation's open question escalation server-side
      // (Phase 3A) — refresh its strip and the list count to match.
      queryClient.invalidateQueries({ queryKey: ["/api/agent/escalations"] });
      if (data && "warning" in data && data.warning === "sent_not_logged") {
        toast({ description: "Sent — but it couldn't be logged to the thread." });
      } else {
        toast({ description: thread?.sendAs ? `Sent as ${thread.sendAs}.` : "Sent." });
      }
    },
    onError: (e: unknown) => {
      let msg = (e instanceof Error ? e.message : String(e)) || "Failed to send reply.";
      const m = msg.match(/^\d+:\s*([\s\S]*)$/);
      if (m) {
        try {
          msg = JSON.parse(m[1]).message || m[1];
        } catch {
          msg = m[1];
        }
      }
      toast({ variant: "destructive", description: msg });
    },
  });

  // The side thread's compose box: the admin answers for the rep, read exactly as
  // the rep's 1:1 text would be (POST side-reply → conversationsTransport.ts).
  const sideReplyMutation = useMutation({
    mutationFn: async ({ id, content }: { id: number; content: string }) => {
      const res = await apiRequest("POST", `/api/agent/conversations/${id}/side-reply`, { content });
      return res.json() as Promise<{ ok: boolean }>;
    },
    onSuccess: () => {
      setSideReply("");
      // The answer lands in this 1:1, may recap or relay in a buyer's thread, and
      // closes the question it answered — refresh every conversation and the list.
      queryClient.invalidateQueries({ queryKey: ["/api/agent/conversations"] });
      queryClient.invalidateQueries({ queryKey: ["/api/agent/escalations"] });
    },
    onError: (e: unknown) => {
      let msg = (e instanceof Error ? e.message : String(e)) || "Failed to send.";
      const m = msg.match(/^\d+:\s*([\s\S]*)$/);
      if (m) {
        try {
          msg = JSON.parse(m[1]).message || m[1];
        } catch {
          msg = m[1];
        }
      }
      toast({ variant: "destructive", description: msg });
    },
  });
  const submitSideReply = () => {
    const text = sideReply.trim();
    if (text && selectedId != null && !sideReplyMutation.isPending) {
      sideReplyMutation.mutate({ id: selectedId, content: text });
    }
  };

  const submitReply = () => {
    const text = reply.trim();
    if (text && selectedId != null && !replyMutation.isPending) {
      replyMutation.mutate({ id: selectedId, content: text });
    }
  };

  const escalationsQuery = useQuery<AgentEscalation[]>({
    queryKey: ["/api/agent/escalations"],
  });
  // To-dos only (lib/clydeEscalations.ts): a rep check or an orientation is the
  // transport's record of what it asked the rep, not something to answer here.
  const escalations = consoleEscalations(escalationsQuery.data ?? []);
  // Every open row, rep checks included: an opening aside reads "waiting on an
  // answer" while its escalation is here, "answered" once it is gone.
  const openEscalationIds = useMemo(() => new Set((escalationsQuery.data ?? []).map((e) => e.id)), [escalationsQuery.data]);
  // Each item sits under the message it came from (Jon, 2026-10-21); the list
  // carries a count per conversation and one line above it filters to them.
  const needsInputCounts = useMemo(() => openCountByConversation(escalations), [escalations]);
  const [needsInputOnly, setNeedsInputOnly] = useState(false);
  const filterNeedsInput = needsInputOnly && needsInputCounts.size > 0;
  const listedConversations = filterNeedsInput ? conversations.filter((c) => needsInputCounts.has(c.id)) : conversations;

  // Catalog for the product_match resolution card's typeahead — fetched only when
  // a held order is actually in the queue (list context; imageData excluded).
  const hasProductMatch = escalations.some((e) => e.type === "product_match");
  // includeHiddenCategories: this is an admin surface — a term may legitimately
  // resolve to a product whose category is currently hidden from customers.
  const productsQuery = useQuery<CatalogProduct[]>({
    queryKey: ["/api/products/active?includeHiddenCategories=1"],
    enabled: hasProductMatch,
  });
  const catalogProducts = productsQuery.data ?? [];

  const resolveEscalation = useMutation({
    mutationFn: ({ id, status }: { id: number; status: "resolved" | "dismissed" }) =>
      apiRequest("PATCH", `/api/agent/escalations/${id}`, { status }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ["/api/agent/escalations"] });
    },
    onError: (e: unknown) => {
      toast({ variant: "destructive", description: e instanceof Error ? e.message : "Failed to update." });
    },
  });

  // ---- Learned-alias suggestions (Phase 2 learn→suggest→approve) ----
  const aliasSuggestionsQuery = useQuery<AliasSuggestion[]>({
    queryKey: ["/api/agent/alias-suggestions"],
  });
  const aliasSuggestions = aliasSuggestionsQuery.data ?? [];

  const decideAlias = useMutation({
    mutationFn: ({ productId, alias, action }: { productId: number; alias: string; action: "approve" | "reject" }) =>
      apiRequest("POST", `/api/products/${productId}/aliases/decide`, { alias, action }),
    onSuccess: (_data, variables) => {
      queryClient.invalidateQueries({ queryKey: ["/api/agent/alias-suggestions"] });
      // An approved alias changes product search + the Settings "Also known as" field.
      queryClient.invalidateQueries({ queryKey: ["/api/products"] });
      toast({
        description:
          variables.action === "approve"
            ? `"${variables.alias}" now matches automatically.`
            : `"${variables.alias}" rejected.`,
      });
    },
    onError: (e: unknown) => {
      toast({ variant: "destructive", description: e instanceof Error ? e.message : "Failed to update alias." });
    },
  });

  // This conversation's items: under their messages, or at the top when theirs
  // is not on screen (lib/clydeEscalations.ts placeEscalations).
  const placed = useMemo(
    () => placeEscalations(escalations, selectedId ?? -1, thread?.messages.map((m) => m.id) ?? []),
    [escalations, selectedId, thread],
  );
  const strip = (e: AgentEscalation, alignRight: boolean) => (
    <EscalationStrip
      key={`escalation-${e.id}`}
      escalation={e}
      products={catalogProducts}
      productsLoading={productsQuery.isLoading}
      onDismiss={() => resolveEscalation.mutate({ id: e.id, status: "dismissed" })}
      dismissing={resolveEscalation.isPending}
      alignRight={alignRight}
    />
  );
  const withStrips = (m: AgentMessage, row: JSX.Element) => {
    const here = placed.byMessage.get(m.id);
    if (!here || !thread) return row;
    const right = attributeMessage(m, conversationTitle(thread.conversation), thread.agentName, thread.thread?.repName, sideThread).side === "right";
    return (
      <div key={m.id}>
        {row}
        <div className={`flex flex-col ${right ? "items-end" : "items-start"}`}>{here.map((e) => strip(e, right))}</div>
      </div>
    );
  };

  // Opening a conversation that needs input brings its first item into view
  // (once per conversation; a ?message= deep link wins).
  const stripScrolledFor = useRef<number | null>(null);
  useEffect(() => {
    if (!thread || selectedId == null || focusMessageId != null || stripScrolledFor.current === selectedId) return;
    const el = messagesRef.current?.querySelector<HTMLElement>('[data-testid="escalation-strip"]');
    if (!el) return;
    stripScrolledFor.current = selectedId;
    el.scrollIntoView({ block: "center" });
  }, [thread, selectedId, focusMessageId, placed]);

  return (
    <div className="max-w-7xl mx-auto p-4 sm:p-6">
      <div className="mb-4">
        <h1 className="text-2xl font-semibold flex items-center gap-2">
          <CowboyHatIcon className="h-6 w-6 text-violet-600" />
          Clyde
        </h1>
        <p className="text-sm text-muted-foreground mt-1">
          Buyer conversations handled by the SMS agent
          {organization?.name ? ` for ${organization.name}` : ""}. Jump in whenever a thread needs a human.
        </p>
      </div>

      {needsInputCounts.size > 0 && (
        <button
          type="button"
          onClick={() => setNeedsInputOnly((v) => !v)}
          aria-pressed={filterNeedsInput}
          className="mb-4 flex w-full items-center gap-2 rounded-md border border-amber-300 bg-amber-50 px-3 py-2 text-left text-sm text-amber-900 hover:bg-amber-100"
          data-testid="needs-input-bar"
        >
          <AlertTriangle className="h-4 w-4 shrink-0 text-amber-600" />
          <span className="font-medium">{needsInputLine(needsInputCounts.size)}</span>
          <span className="ml-auto text-xs underline underline-offset-2">
            {filterNeedsInput ? "Show all conversations" : "Show only these"}
          </span>
        </button>
      )}

      {aliasSuggestions.length > 0 && (
        <Card className="mb-4 border-violet-200 bg-violet-50/30">
          <CardHeader className="pb-3">
            <CardTitle className="flex items-center gap-2 text-base">
              <Tag className="h-4 w-4 text-violet-600" />
              Suggested aliases
              <span className="rounded-full bg-violet-100 px-2 text-xs font-medium text-violet-900">
                {aliasSuggestions.length}
              </span>
            </CardTitle>
            <CardDescription>
              Terms buyers used that the agent worked out. Approve one and it matches automatically from then on.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-2">
            {aliasSuggestions.map((s) => (
              <div
                key={`${s.productId}:${s.alias}`}
                data-testid="alias-suggestion-item"
                className="flex items-center justify-between gap-3 rounded-md border bg-white p-2"
              >
                <div className="flex-1 text-sm">
                  <span className="font-medium">“{s.alias}”</span>
                  <span className="text-muted-foreground"> → </span>
                  <span>{s.productName}</span>
                  <span className="ml-2 text-xs text-muted-foreground">
                    heard {s.seenCount}×{s.at ? ` · first ${relativeTime(s.at)}` : ""}
                  </span>
                </div>
                <div className="flex shrink-0 gap-1">
                  <Button
                    size="sm"
                    disabled={decideAlias.isPending}
                    onClick={() => decideAlias.mutate({ productId: s.productId, alias: s.alias, action: "approve" })}
                    data-testid="alias-suggestion-approve"
                  >
                    Approve
                  </Button>
                  <Button
                    size="sm"
                    variant="ghost"
                    disabled={decideAlias.isPending}
                    onClick={() => decideAlias.mutate({ productId: s.productId, alias: s.alias, action: "reject" })}
                    data-testid="alias-suggestion-reject"
                  >
                    Reject
                  </Button>
                </div>
              </div>
            ))}
          </CardContent>
        </Card>
      )}

      <div className="grid grid-cols-1 gap-4 md:grid-cols-[320px_minmax(0,1fr)]">
        {/* Conversation list */}
        <Card className="flex min-w-0 flex-col md:h-[calc(100vh-220px)]">
          <CardHeader className="pb-3">
            <CardTitle className="text-base">Conversations</CardTitle>
            <CardDescription>
              {conversationsQuery.isLoading
                ? "Loading…"
                : filterNeedsInput
                  ? `${listedConversations.length} need${listedConversations.length === 1 ? "s" : ""} your input`
                  : `${conversations.length} buyer${conversations.length === 1 ? "" : "s"} · open threads first`}
            </CardDescription>
          </CardHeader>
          <CardContent className="flex-1 overflow-y-auto p-0">
            {conversationsQuery.isLoading ? (
              <p className="p-4 text-sm text-muted-foreground">Loading…</p>
            ) : conversationsQuery.isError ? (
              <p className="p-4 text-sm text-red-600">Failed to load conversations.</p>
            ) : conversations.length === 0 ? (
              <div className="p-6 text-center text-sm text-muted-foreground">
                <Inbox className="h-8 w-8 mx-auto mb-2 opacity-40" />
                No conversations yet. When a buyer texts the agent, the thread appears here.
              </div>
            ) : (
              <ul className="divide-y">
                {listedConversations.map((c) => {
                  const active = c.id === selectedId;
                  const needsInput = needsInputCounts.get(c.id) ?? 0;
                  return (
                    <li key={c.id}>
                      <button
                        type="button"
                        onClick={() => selectConversation(c.id)}
                        data-testid="conversation-list-item"
                        data-thread-status={c.thread?.status ?? "none"}
                        className={`w-full text-left px-4 py-3 transition-colors ${
                          active ? "bg-violet-50" : "hover:bg-gray-50"
                        }`}
                      >
                        <div className="flex items-center justify-between gap-2">
                          <span className="font-medium truncate">{conversationTitle(c)}</span>
                          <span className="flex shrink-0 items-center gap-1.5">
                            {needsInput > 0 && (
                              <span
                                className="inline-flex items-center gap-0.5 rounded-full bg-amber-200 px-1.5 py-0.5 text-[10px] font-medium text-amber-900"
                                title={`${needsInput} item${needsInput === 1 ? "" : "s"} need${needsInput === 1 ? "s" : ""} your input`}
                                aria-label={`${needsInput} need${needsInput === 1 ? "s" : ""} input`}
                                data-testid="conversation-needs-input"
                              >
                                <AlertTriangle className="h-2.5 w-2.5" />
                                {needsInput}
                              </span>
                            )}
                            <span className="text-xs text-muted-foreground">{relativeTime(c.lastMessageAt)}</span>
                          </span>
                        </div>
                        <div className="mt-0.5 flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-muted-foreground">
                          <span className="inline-flex items-center gap-1">
                            <Phone className="h-3 w-3" />
                            {displayPhone(c.customerPhone)}
                          </span>
                          {c.thread && (
                            <span
                              className={`rounded px-1.5 py-0.5 text-[10px] font-medium ${
                                c.thread.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
                              }`}
                            >
                              {c.thread.status === "active" ? "Open" : "Closed"} · {channelLabel(c.thread.channel)}
                              {c.thread.repAddress ? ` · with ${c.thread.repName || "rep"}` : " · 1:1"}
                            </span>
                          )}
                        </div>
                      </button>
                    </li>
                  );
                })}
              </ul>
            )}
          </CardContent>
        </Card>

        {/* Thread */}
        <Card className="flex min-w-0 flex-col md:h-[calc(100vh-220px)]">
          {selectedId == null ? (
            <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
              Select a conversation to view the thread.
            </div>
          ) : threadQuery.isLoading ? (
            <div className="flex flex-1 items-center justify-center p-6 text-sm text-muted-foreground">
              Loading…
            </div>
          ) : threadQuery.isError || !thread ? (
            <div className="flex flex-1 items-center justify-center p-6 text-sm text-red-600">
              Failed to load conversation.
            </div>
          ) : (
            <>
              <CardHeader className="border-b pb-3">
                <CardTitle className="text-base">{conversationTitle(thread.conversation)}</CardTitle>
                <CardDescription className="flex flex-col gap-1" data-testid="thread-header">
                  <span className="flex items-center gap-1">
                    <Phone className="h-3 w-3" /> {displayPhone(thread.conversation.customerPhone)}
                  </span>
                  {thread.thread ? (
                    <span className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs">
                      <span
                        className={`rounded px-1.5 py-0.5 font-medium ${
                          thread.thread.status === "active" ? "bg-emerald-50 text-emerald-700" : "bg-gray-100 text-gray-500"
                        }`}
                        data-testid="thread-status"
                      >
                        {thread.thread.status === "active" ? "Open" : "Closed"}
                      </span>
                      <span>{channelLabel(thread.thread.channel)}</span>
                      <span className="inline-flex items-center gap-1" data-testid="thread-members">
                        <Users className="h-3 w-3" />
                        {conversationTitle(thread.conversation)}
                        {thread.thread.repAddress
                          ? ` · ${thread.thread.repName || "Rep"} (${displayPhone(thread.thread.repAddress)})`
                          : " · no rep (1:1)"}
                        {` · ${thread.agentName || "Clyde"}`}
                      </span>
                      <span>{startedByLabel(thread.thread.createdBy)}{thread.thread.createdAt ? ` ${relativeTime(thread.thread.createdAt)}` : ""}</span>
                    </span>
                  ) : sideThread ? (
                    <span className="text-xs">Every text {thread.agentName || "Clyde"} sends the rep about a buyer, and their answers</span>
                  ) : (
                    <span className="text-xs">No Clyde thread yet — texts to the org's number land here 1:1.</span>
                  )}
                </CardDescription>
              </CardHeader>
              <CardContent className="flex-1 space-y-3 overflow-y-auto py-4" ref={messagesRef}>
                {sideThread && (
                  <div className="flex items-start gap-2 rounded-md border border-dashed border-amber-400 bg-amber-50 px-3 py-2 text-xs text-amber-900" data-testid="side-thread-banner">
                    <Lock className="mt-0.5 h-3.5 w-3.5 shrink-0" />
                    <span>
                      {thread.agentName || "Clyde"}'s private 1:1 with {sideRepName || "the rep"}. Buyers never see it. Each text opens the buyer's thread, and an answer here counts as {sideRepName || "the rep"}'s reply.
                    </span>
                  </div>
                )}
                {placed.unplaced.length > 0 && (
                  <div className="flex flex-col gap-1" data-testid="escalations-unplaced">
                    <span className="text-[11px] text-muted-foreground">From earlier in the thread</span>
                    {placed.unplaced.map((e) => strip(e, false))}
                  </div>
                )}
                {thread.messages.length === 0 ? (
                  <p className="text-center text-sm text-muted-foreground">No messages in this thread.</p>
                ) : (
                  thread.messages.map((m) => withStrips(m, (() => {
                    const buyerName = conversationTitle(thread.conversation);
                    const attr = attributeMessage(m, buyerName, thread.agentName, thread.thread?.repName, sideThread);
                    const link = asideLink(m);
                    const focused = m.id === focusMessageId;
                    if (isReactionRow(m)) {
                      // A tapback: one small marker, never a bubble.
                      return (
                        <div key={m.id} data-message-id={m.id} data-testid="agent-reaction" className="text-center">
                          <span className="inline-flex items-center gap-1 text-[11px] text-gray-400" title={m.content}>
                            <Smile className="h-3 w-3" />
                            {attr.label} reacted{m.content ? `: ${m.content.length > 48 ? `${m.content.slice(0, 48)}…` : m.content}` : ""}
                          </span>
                        </div>
                      );
                    }
                    if (attr.side === "center") {
                      return (
                        <div key={m.id} data-message-id={m.id} data-testid="agent-muted-row" className="text-center">
                          <span className={`text-xs ${attr.labelClass}`}>
                            {attr.label}: {m.content}
                          </span>
                        </div>
                      );
                    }
                    const alignRight = attr.side === "right";
                    return (
                      <div
                        key={m.id}
                        data-message-id={m.id}
                        data-testid="agent-message"
                        data-role={m.role}
                        className={`flex flex-col ${alignRight ? "items-end" : "items-start"}`}
                      >
                        <div className={`mb-1 flex items-center gap-1 text-xs ${attr.labelClass}`}>
                          <attr.Icon className="h-3 w-3" />
                          <span className="font-medium">{attr.label}</span>
                        </div>
                        {link ? (
                          <button
                            type="button"
                            onClick={() => jumpTo(link.conversationId, link.messageId)}
                            data-testid={link.to === "side" ? "agent-aside-open-side" : "agent-aside-open-buyer"}
                            title={link.to === "side" ? "Open the 1:1 with the rep" : "Open the buyer's thread"}
                            className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-left text-sm transition-shadow hover:shadow-md ${attr.bubbleClass} ${
                              focused ? "ring-2 ring-violet-400 ring-offset-1" : ""
                            }`}
                          >
                            {m.content}
                            <span className={`mt-2 block text-xs font-medium underline underline-offset-2 ${link.to === "side" ? "text-amber-800" : "text-violet-700"}`}>
                              {link.to === "side"
                                ? `Open the 1:1 with ${asideRepName(m, thread.thread?.repName) || "the rep"} →`
                                : `About ${typeof (m.metadata as Record<string, unknown> | null)?.accountName === "string" ? (m.metadata as Record<string, string>).accountName : "the buyer"} · open their thread →`}
                            </span>
                          </button>
                        ) : (
                          <div
                            className={`max-w-[85%] whitespace-pre-wrap rounded-lg px-3 py-2 text-sm ${attr.bubbleClass} ${
                              focused ? "ring-2 ring-violet-400 ring-offset-1" : ""
                            }`}
                          >
                            {renderMessageContent(m, (id) => ordersPageOrderPath(adminBase, id), openOrder)}
                          </div>
                        )}
                        {(() => {
                          const status = asideStatus(m, openEscalationIds);
                          if (!status) return null;
                          return status === "open" ? (
                            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-amber-100 px-2 py-0.5 text-[11px] font-medium text-amber-900" data-testid="aside-status-open">
                              <AlertCircle className="h-3 w-3" />
                              Needs an answer{sideThread ? " — answer below" : ""}
                            </span>
                          ) : (
                            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-50 px-2 py-0.5 text-[11px] font-medium text-emerald-800" data-testid="aside-status-answered">
                              <CheckCircle2 className="h-3 w-3" />
                              Answered
                            </span>
                          );
                        })()}
                        <span
                          className={`mt-1 text-[10px] ${attr.note ? "text-amber-700" : "text-muted-foreground"}`}
                          title={new Date(m.createdAt).toLocaleString()}
                          data-testid={attr.note ? "agent-aside-note" : undefined}
                        >
                          {attr.note ? `${attr.note} · ` : ""}
                          {relativeTime(m.createdAt)}
                        </span>
                      </div>
                    );
                  })()))
                )}
              </CardContent>
              <div className="border-t p-3">
                {sideThread ? (
                  <div data-testid="compose-side-thread">
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        submitSideReply();
                      }}
                      className="flex items-end gap-2"
                    >
                      <Textarea
                        value={sideReply}
                        onChange={(e) => setSideReply(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            submitSideReply();
                          }
                        }}
                        placeholder={sideComposePlaceholder(sideRepName)}
                        rows={2}
                        maxLength={1600}
                        className="resize-none"
                        data-testid="side-reply-input"
                      />
                      <Button
                        type="submit"
                        disabled={!sideReply.trim() || sideReplyMutation.isPending}
                        data-testid="side-reply-send"
                        className="gap-1"
                      >
                        <Send className="h-4 w-4" />
                        {sideReplyMutation.isPending ? "Sending…" : "Answer"}
                      </Button>
                    </form>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      {thread.agentName || "Clyde"} reads it as {sideRepName || "the rep"}'s reply, the same as a text from their phone. Nothing is texted to {sideRepName || "the rep"}.
                    </p>
                  </div>
                ) : thread.sendAs ? (
                  <>
                    <form
                      onSubmit={(e) => {
                        e.preventDefault();
                        submitReply();
                      }}
                      className="flex items-end gap-2"
                    >
                      <Textarea
                        value={reply}
                        onChange={(e) => setReply(e.target.value)}
                        onKeyDown={(e) => {
                          if (e.key === "Enter" && !e.shiftKey) {
                            e.preventDefault();
                            submitReply();
                          }
                        }}
                        placeholder={`Send as ${thread.sendAs}…`}
                        rows={2}
                        maxLength={1600}
                        className="resize-none"
                        data-testid="takeover-input"
                      />
                      <Button
                        type="submit"
                        disabled={!reply.trim() || replyMutation.isPending}
                        data-testid="takeover-send"
                        className="gap-1"
                      >
                        <Send className="h-4 w-4" />
                        {replyMutation.isPending ? "Sending…" : `Send as ${thread.sendAs}`}
                      </Button>
                    </form>
                    <p className="mt-1 text-[11px] text-muted-foreground">
                      Goes out from {thread.agentName || "Clyde"}'s number as "{thread.sendAs}: …" and is logged as {thread.sendAs}'s message — never as {thread.agentName || "Clyde"}.
                    </p>
                  </>
                ) : (
                  <div className="flex flex-wrap items-center justify-between gap-2 text-xs text-muted-foreground" data-testid="compose-needs-name">
                    <span>
                      To send from here, this thread needs a rep's name — the buyer sees messages as "Name: …". FreshOrder sets
                      the supervisor's name for your account; ask us to add it.
                    </span>
                  </div>
                )}
              </div>
            </>
          )}
        </Card>
      </div>
    </div>
  );
}
